import { type ImputationMethod } from '@ebalance/proto/bffuser/v1/imputation_policy_model_pb'
import { useTranslation } from 'react-i18next'

import { Box, Flex, TertiaryButton, Text } from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaChevronUpRegular,
} from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { iconSize } from '~/constants/iconSize'
import { METHOD_I18N_KEYS } from '~/pages/BalanceImputationPolicy/constants/defaults'

type Props = {
  value: ImputationMethod[]
  onMoveUp: (index: number) => void
  onMoveDown: (index: number) => void
  /** 保存中は操作させない (= 保存の直列化。useImputationPolicy の godoc 参照)。 */
  disabled: boolean
}

/**
 * 並べ替え 1 ボタン。 design `.ecl-reorder-actions` の
 * `btn-tertiary btn-square btn-sm` 相当 (アイコンのみ + Tooltip)。
 *
 * **正方形は `width` / `minWidth` / `paddingX` で明示する。** DS に design の
 * `.btn-square` (aspect-ratio 1/1) にあたる variant が無く、 `size="sm"` の recipe は
 * `height 32px / minWidth 32px / paddingX 12px` なので、 指定しないと
 * 16(icon) + 24(paddingX) + 2(border) = **42x32 の横長**になる。
 * 高さは recipe が供給するので `height` は書かない。
 * 同形の先行実装は `components/common/filters/DateStepper.tsx` /
 * `SpotPageHeader.tsx`。
 */
const ReorderButton = ({
  direction,
  label,
  ariaLabel,
  disabled,
  onClick,
}: {
  direction: 'up' | 'down'
  /** ツールチップ (と保険の title) の文言。「上へ」/「下へ」。 */
  label: string
  /**
   * 読み上げの名前。 行の名前を含める (「上へ」 だけだと同じ名前のボタンが行数ぶん並び、
   * どの行のボタンか分からない。 UI 辞書 components/reorder-actions.md)。
   */
  ariaLabel: string
  disabled: boolean
  onClick: () => void
}): React.ReactElement => {
  // disabled の button は Tooltip を取りこぼすため span で受ける必要があるが、
  // その作法は共通ラッパ [[ButtonTooltip]] に一本化されている
  // (`components/common/ButtonTooltip.tsx` の JSDoc、 2026-08-17 デザイナー既定:
  // 「DS Tooltip が主役 + native title は保険」)。 ここで span を再実装しない。
  return (
    <ButtonTooltip content={label}>
      <TertiaryButton
        width="32px"
        minWidth="32px"
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onClick}
        paddingX={0}
        size="sm"
        title={label}
      >
        {direction === 'up' ? (
          <FaChevronUpRegular
            boxSize={iconSize.gridInline}
            aria-hidden="true"
          />
        ) : (
          <FaChevronDownRegular
            boxSize={iconSize.gridInline}
            aria-hidden="true"
          />
        )}
      </TertiaryButton>
    </ButtonTooltip>
  )
}

/**
 * 発電側の補完方式フォールバック順リスト (spec AC-007 / AC-008 / AC-009)。
 * design/balance/imputation-policy.html の `.ipe-order-list` 相当で、
 * 順番バッジ + ▲▼ ボタンによる入れ替えを提供する。
 *
 * ボタン方式のため「ドラッグ中の途中状態」 が構造的に存在せず、
 * AC-009 NOTE (ドラッグ中は保存しない) は自動的に満たされる。
 */
export const GenerationMethodOrderList = ({
  value,
  onMoveUp,
  onMoveDown,
  disabled,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Box flexDirection="column" gap={2} display="flex" maxWidth="720px">
      {value.map((method, index) => (
        <Flex
          key={method}
          alignItems="center"
          gap={3}
          padding={3}
          borderWidth="1px"
          borderColor="Border.midEmphasis"
          borderRadius="lg"
          // design `balance/imputation-policy.html` の `.ipe-order-row` は
          // `background: var(--surface-primary)` を持つ。 実装は枠線と角丸だけを
          // 写して地色が抜けており、 ページの地の上に枠だけが浮いていた
          // (2026-09-01 デザイナー指摘)。
          //
          // **行に付ける** (リストのコンテナではない)。 コンテナ側に付けると
          // 行間の `gap` まで塗られ、 分離したカードでなく 1 枚の白ブロックに見える。
          backgroundColor="Surface.primary"
        >
          <Flex
            textStyle="captionStrong"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            width="24px"
            height="24px"
            // Text.inverse は ecloud に存在しないトークン (= 解決されず番号が
            // アクセント背景に埋もれる)。反転文字は highEmphasisInverse が正。
            color="Text.highEmphasisInverse"
            borderRadius="full"
            backgroundColor="Surface.accentPrimary"
          >
            {index + 1}
          </Flex>
          <Box flex="1" minWidth="0">
            {/* design `.ipe-method-name` は `font-weight: 700`
                (`balance/imputation-policy.html` L39)。 実装は太字指定が抜けていて
                説明文と同じ太さで並び、 方式名が見出しとして立っていなかった
                (2026-09-02 デザイナー指摘)。

                役割 token は `radioCardTitle` (= md / bold)。 需要タブが同じ「方式名」に
                同じ役割を当てており (`DemandMethodRadio.tsx`)、 2 タブで一致する。
                `fontSize` + `fontWeight` の手書きは `ebalance/no-adhoc-typography` 違反。 */}
            <Text textStyle="radioCardTitle" color="Text.highEmphasis">
              {t(METHOD_I18N_KEYS[method].name)}
            </Text>
            <Text color="Text.midEmphasis" fontSize="sm">
              {t(METHOD_I18N_KEYS[method].description)}
            </Text>
          </Box>
          {/* design `.ecl-reorder-actions` (`css/components.css`) +
              `imputation-policy.html` の並べ替えボタン:
              `btn btn-tertiary btn-square btn-sm` + 16px の chevron アイコン。
              geometry と icon サイズは「3 画面で同一に保つため」 共通クラスへ
              切り出されている (design 側 2026-07-29 のメモ)。

              旧実装は `SecondaryButton` に **文字の ↑ / ↓** を入れていた
              (= アイコンですらなく、 ボタン種別も design の tertiary と不一致)。
              2026-09-01 デザイナー指摘「↑↓ アイコンは DS 準拠、 ボタンも DS 準拠」。 */}
          <Flex alignItems="center" flexShrink={0} gap={1}>
            <ReorderButton
              direction="up"
              ariaLabel={t(
                'page:balanceImputationPolicy.generation.moveUpItem',
                {
                  name: t(METHOD_I18N_KEYS[method].name),
                },
              )}
              disabled={disabled || index === 0}
              label={t('page:balanceImputationPolicy.generation.moveUp')}
              onClick={() => onMoveUp(index)}
            />
            <ReorderButton
              direction="down"
              ariaLabel={t(
                'page:balanceImputationPolicy.generation.moveDownItem',
                { name: t(METHOD_I18N_KEYS[method].name) },
              )}
              disabled={disabled || index === value.length - 1}
              label={t('page:balanceImputationPolicy.generation.moveDown')}
              onClick={() => onMoveDown(index)}
            />
          </Flex>
        </Flex>
      ))}
    </Box>
  )
}
